import { useEffect, useState, useCallback } from "react";
import {
  FiSearch,
  FiTrash2,
  FiX,
  FiAlertCircle,
  FiEye,
  FiUsers,
  FiDollarSign,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { makeRequestAuth } from "../../../_config/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import Spinner from "../../../utils/Loader";

function AllUsers() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    id: null,
    isDisable: null,
    type: "",
    message: "",
  });

  const [walletModal, setWalletModal] = useState(false);
  const [walletUser, setWalletUser] = useState(null);
  const [walletData, setWalletData] = useState({ usdt: 0, token: 0 });
  const [walletAmount, setWalletAmount] = useState("");
  const [walletType, setWalletType] = useState("usdt");

  const fetchUsers = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth("post", "/admin/getUserList", {
          keyWord: searchTerm,
          sortOrder: "desc",
          sortBy: "createdAt",
          pageNo: page,
          size: pageSize,
        });
        const { list, total } = response.data;
        setUsers(list || []);
        setTotal(total || 0);
      } catch (err) {
        showErrorToast(err.message || "Failed to fetch users");
      } finally {
        setIsLoading(false);
      }
    },
    [searchTerm, pageSize],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchUsers(currentPage);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [currentPage, searchTerm, fetchUsers]);

  const handleStatusToggle = async () => {
    const { id, isDisable } = confirmModal;
    try {
      const newStatus = !isDisable;
      await makeRequestAuth("post", "/admin/updateUserStatus", { id });
      setUsers((prev) =>
        prev.map((b) => (b._id === id ? { ...b, isDisable: newStatus } : b)),
      );
      if (newStatus) {
        showErrorToast("User marked as inactive");
      } else {
        showSuccessToast("User marked as active");
      }
    } catch {
      showErrorToast("Failed to update status");
    } finally {
      setConfirmModal({
        open: false,
        id: null,
        isDisable: null,
        type: "",
        message: "",
      });
    }
  };

  const handleDelete = async () => {
    const { id } = confirmModal;
    try {
      await makeRequestAuth("post", "/admin/deleteUser", { id });
      setUsers((prev) => prev.filter((c) => c._id !== id));
      setTotal((prev) => prev - 1);
      showSuccessToast("User deleted successfully!");
    } catch (err) {
      showErrorToast(err.message || "Delete failed");
    } finally {
      setConfirmModal({
        open: false,
        id: null,
        isDisable: null,
        type: "",
        message: "",
      });
    }
  };

  const openStatusModal = (id, currentStatus) => {
    setConfirmModal({
      open: true,
      id,
      isDisable: currentStatus,
      type: "status",
      message: currentStatus
        ? "Are you sure you want to deactivate this user?"
        : "Are you sure you want to activate this user?",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Referral chain page pe navigate karo — koi API call nahi
  const handleViewReferralChain = (user) => {
    navigate(
      `/users/referral-chain/${user._id}/${encodeURIComponent(user.name)}`,
    );
  };

  const totalPages = Math.ceil(total / pageSize);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const fetchWallet = async (userId) => {
    try {
      const res = await makeRequestAuth(
        "get",
        `/admin/getUserWallet?userId=${userId}`,
      );

      setWalletData(res.data);
    } catch {
      showErrorToast("Wallet fetch failed");
    }
  };

  const handleWalletOpen = async (user) => {
    setWalletUser(user);
    setWalletModal(true);
    fetchWallet(user._id);
  };

  const handleWalletUpdate = async (type) => {
    try {
      const amount = Number(walletAmount);

      if (!amount) {
        showErrorToast("Enter amount");
        return;
      }

      const payload = {
        userId: walletUser._id,
        usdt: walletType === "usdt" ? (type === "add" ? amount : -amount) : 0,
        token: walletType === "token" ? (type === "add" ? amount : -amount) : 0,
      };

      const res = await makeRequestAuth(
        "post",
        "/admin/updateUserWallet",
        payload,
      );

      setWalletData(res.data);

      showSuccessToast("Wallet updated");

      setWalletAmount("");
    } catch {
      showErrorToast("Wallet update failed");
    }
  };

  const formatBalance = (value) => {
    if (value % 1 !== 0) {
      return Number(value).toFixed(2);
    }
    return value;
  };

  // if (isLoading) return <Spinner />;

  return (
    <div>
      {isLoading && (
        <div className="absolute inset-0 bg-white/60 dark:bg-black/40 z-50 flex items-center justify-center">
          <Spinner />
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 p-4">
        <ToastContainer position="top-right" autoClose={3000} />

        <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
          {/* HEADER */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                All Users
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Total {total} users found
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
              />
            </div>
          </div>

          {/* TABLE */}
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full table-auto text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                    <th className="py-4 px-6 w-16 text-center">#</th>
                    <th className="py-4 px-6 min-w-[80px]">Name</th>
                    <th className="py-4 px-6 min-w-[80px]">Mobile No.</th>
                    <th className="py-4 px-6 min-w-[250px]">Email</th>
                    <th className="py-4 px-6 min-w-[150px]">
                      User Referral Code
                    </th>
                    <th className="py-4 px-6 min-w-[150px]">
                      Joined Via Referral
                    </th>
                    <th className="py-4 px-6 min-w-[120px]">Date</th>
                    <th className="py-4 px-6 min-w-[140px]">Status</th>
                    <th className="py-4 px-6 min-w-[180px] text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {users.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-10 text-gray-500 dark:text-gray-400"
                      >
                        <p className="text-lg font-medium">No Users Found</p>
                        <p className="text-sm mt-1">
                          Try adding a new user to get started.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr
                        key={user._id}
                        className="hover:bg-blue-50/50 dark:hover:bg-gray-700/30 group"
                      >
                        <td className="py-4 px-6 text-center text-gray-500 dark:text-gray-400 font-medium">
                          {(currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 flex-shrink-0">
                              {user.image ? (
                                <img
                                  src={user.image}
                                  alt={user.name}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    e.target.src =
                                      "https://ui-avatars.com/api/?name=" +
                                      user.name;
                                  }}
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 text-white font-bold text-xs">
                                  {user.name?.charAt(0).toUpperCase() || "U"}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col leading-tight">
                              <span className="text-gray-900 dark:text-white font-semibold">
                                {user.userName}
                              </span>
                              <span className="text-gray-500 text-sm">
                                {user.name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                          {user.mobile}
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                          {user.email}
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                          {user.referralCode}
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                          {user.refByCode}
                        </td>
                        <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                          {/* {formatDate(user.createdAt)} */}
                          {moment(user.createdAt).format(
                            "DD MMM YYYY, hh:mm:ss A",
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() =>
                                openStatusModal(user._id, user.isDisable)
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                !user.isDisable
                                  ? "bg-emerald-500"
                                  : "bg-gray-300 dark:bg-gray-600"
                              }`}
                              title={
                                !user.isDisable
                                  ? "Click to Deactivate"
                                  : "Click to Activate"
                              }
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  !user.isDisable
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                            <span
                              className={`text-xs font-semibold uppercase ${
                                !user.isDisable
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-gray-500 dark:text-gray-400"
                              }`}
                            >
                              {!user.isDisable ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Referral Chain - sirf navigate, koi API nahi */}
                            <button
                              onClick={() => handleViewReferralChain(user)}
                              className="p-2 rounded-lg text-purple-500 hover:text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:hover:text-purple-300 dark:hover:bg-purple-900/20 transition"
                              title="View Referral Chain"
                            >
                              <FiUsers size={18} />
                            </button>
                            {/* Preview */}
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowModal(true);
                              }}
                              className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 transition"
                              title="Preview User"
                            >
                              <FiEye size={18} />
                            </button>

                            <button
                              onClick={() => handleWalletOpen(user)}
                              className="p-2 rounded-lg text-green-500 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition"
                              title="Manage Wallet"
                            >
                              <FiDollarSign size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PAGINATION */}
          {total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                Showing{" "}
                <span className="text-gray-900 dark:text-white font-bold">
                  {(currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="text-gray-900 dark:text-white font-bold">
                  {Math.min(currentPage * pageSize, total)}
                </span>{" "}
                of{" "}
                <span className="text-gray-900 dark:text-white font-bold">
                  {total}
                </span>{" "}
                entries
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm"
                >
                  Previous
                </button>
                <div className="hidden sm:flex gap-1">
                  {[...Array(totalPages).keys()].slice(0, 5).map((n) => (
                    <button
                      key={n + 1}
                      onClick={() => goToPage(n + 1)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition ${
                        currentPage === n + 1
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                          : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      {n + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* VIEW USER MODAL */}
        {showModal && selectedUser && (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white dark:bg-[#1E2738] rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="h-32 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 relative">
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 text-white rounded-full transition-all"
                >
                  <FiX size={20} />
                </button>
              </div>
              <div className="relative px-8 pb-8">
                <div className="absolute -top-12 left-8">
                  <div className="h-24 w-24 rounded-2xl border-4 border-white dark:border-[#1E2738] overflow-hidden shadow-xl bg-gray-100 dark:bg-gray-800">
                    {selectedUser.image ? (
                      <img
                        src={selectedUser.image}
                        alt={selectedUser.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 text-white text-3xl font-bold">
                        {selectedUser.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pt-16">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedUser.name}
                    </h2>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                        !selectedUser.isDisable
                          ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30"
                          : "bg-red-100 text-red-600 dark:bg-red-900/30"
                      }`}
                    >
                      {!selectedUser.isDisable ? "Active" : "Disabled"}
                    </span>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    {selectedUser.email}
                  </p>
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Referral Code
                    </span>
                    <p className="text-lg font-mono font-bold text-purple-600 dark:text-purple-400">
                      {selectedUser.referralCode || "NONE"}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      KYC Status
                    </span>
                    <p
                      className={`text-lg font-bold ${selectedUser.isKyc ? "text-blue-500" : "text-amber-500"}`}
                    >
                      {selectedUser.isKyc ? "Verified" : "Pending"}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Joined Date
                    </span>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {formatDate(selectedUser.createdAt)}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Device ID
                    </span>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                      {selectedUser.deviceId || "Not Linked"}
                    </p>
                  </div>
                </div>
                {selectedUser.refByCode && (
                  <div className="mt-4 p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 flex items-center justify-between">
                    <span className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                      Referred By:
                    </span>
                    <span className="font-bold text-blue-800 dark:text-blue-300">
                      {selectedUser.refByCode}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CONFIRMATION MODAL */}
        {confirmModal.open && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fadeIn">
            <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center border border-gray-200 dark:border-gray-600">
              <div
                className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${
                  confirmModal.type === "delete"
                    ? "bg-red-100 dark:bg-red-900/30 text-red-600"
                    : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600"
                }`}
              >
                {confirmModal.type === "delete" ? (
                  <FiTrash2 size={32} />
                ) : (
                  <FiAlertCircle size={32} />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {confirmModal.type === "delete"
                  ? "Delete User?"
                  : "Change Status?"}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                {confirmModal.message}
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() =>
                    setConfirmModal({
                      open: false,
                      id: null,
                      isDisable: null,
                      type: "",
                      message: "",
                    })
                  }
                  className="flex-1 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    confirmModal.type === "delete"
                      ? handleDelete
                      : handleStatusToggle
                  }
                  className={`flex-1 px-5 py-2.5 rounded-xl text-white font-medium shadow-lg transition ${
                    confirmModal.type === "delete"
                      ? "bg-red-600 hover:bg-red-700 shadow-red-500/30"
                      : confirmModal.isDisable
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {confirmModal.type === "delete" ? "Delete" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        {walletModal && walletUser && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 transition-all">
            <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl w-full max-w-md shadow-2xl border border-gray-100 dark:border-gray-700/50">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white tracking-tight">
                  Wallet{" "}
                  <span className="text-blue-500 text-lg font-medium block opacity-80">
                    {walletUser.name}
                  </span>
                </h2>
                <button
                  onClick={() => setWalletModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Balance Cards */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">
                    USDT Balance
                  </p>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    ${formatBalance(walletData.usdt)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">
                    Token Balance
                  </p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {formatBalance(walletData.token)}
                  </p>
                </div>
              </div>

              {/* Input Section */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">
                    Select Asset
                  </label>
                  <select
                    value={walletType}
                    onChange={(e) => setWalletType(e.target.value)}
                    className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                  >
                    <option value="usdt">USDT</option>
                    <option value="token">Token</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">
                    Amount
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(e.target.value)}
                    className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white font-mono text-lg"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => handleWalletUpdate("add")}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
                >
                  Deposit
                </button>

                <button
                  onClick={() => handleWalletUpdate("remove")}
                  className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-semibold py-3 rounded-xl shadow-lg shadow-rose-500/20 transition-all active:scale-95"
                >
                  Withdraw
                </button>
              </div>

              <button
                onClick={() => setWalletModal(false)}
                className="mt-6 w-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllUsers;
